import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { Phone, MapPin, AlertTriangle, ArrowLeft, Heart, Waves, Activity, Anchor, Camera, X, Globe, Map } from 'lucide-react';
import { compressImage } from '../utils/imageCompression';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import EmmeGuide, { useEmmeGuide, EmmeHelpButton } from './EmmeGuide';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Numeri Guardia Costiera per paese
const COAST_GUARD_NUMBERS = {
  'IT': { country: 'Italia', number: '1530', name: 'Guardia Costiera' },
  'ES': { country: 'Spagna', number: '900 202 202', name: 'Salvamento Marítimo' },
  'FR': { country: 'Francia', number: '196', name: 'CROSS' },
  'GR': { country: 'Grecia', number: '108', name: 'Λιμενικό Σώμα' },
  'HR': { country: 'Croazia', number: '195', name: 'Lučka Kapetanija' },
  'PT': { country: 'Portogallo', number: '112', name: 'Autoridade Marítima' },
  'MT': { country: 'Malta', number: '112', name: 'Armed Forces of Malta' },
  'SI': { country: 'Slovenia', number: '112', name: 'Uprava za pomorstvo' },
  'ME': { country: 'Montenegro', number: '112', name: 'Uprava pomorske sigurnosti' },
  'AL': { country: 'Albania', number: '112', name: 'Policia Kufitare' },
  'GB': { country: 'Regno Unito', number: '999', name: 'HM Coastguard' },
  'IE': { country: 'Irlanda', number: '112', name: 'Irish Coast Guard' },
  'DE': { country: 'Germania', number: '124 124', name: 'Maritime Rescue' },
  'NL': { country: 'Paesi Bassi', number: '112', name: 'Kustwacht' },
  'BE': { country: 'Belgio', number: '112', name: 'Kustwacht' },
  'DK': { country: 'Danimarca', number: '112', name: 'Søværnets Operative Kommando' },
  'SE': { country: 'Svezia', number: '112', name: 'Sjöfartsverket' },
  'NO': { country: 'Norvegia', number: '112', name: 'Hovedredningssentralen' },
  'FI': { country: 'Finlandia', number: '112', name: 'Rajavartiolaitos' },
  'PL': { country: 'Polonia', number: '112', name: 'Morska Służba Poszukiwania' },
  'CY': { country: 'Cipro', number: '112', name: 'JRCC Cyprus' },
  'TR': { country: 'Turchia', number: '158', name: 'Sahil Güvenlik' },
  'TN': { country: 'Tunisia', number: '71 754 848', name: 'Office de la Marine Marchande' },
  'EG': { country: 'Egitto', number: '122', name: 'Egyptian Navy' },
  'US': { country: 'USA', number: '911', name: 'US Coast Guard' },
  'AU': { country: 'Australia', number: '000', name: 'Australian Maritime Safety' },
  'DEFAULT': { country: 'Internazionale', number: '112', name: 'Emergenza Europea' }
};

const categories = [
  { name: 'Tartarughe', image: '/sos-icons/tartarughe.jpg' },
  { name: 'Foche', image: '/sos-icons/foche.jpg' },
  { name: 'Cetacei', image: '/sos-icons/cetacei.jpg' },
  { name: 'Uccelli Marini', image: '/sos-icons/uccelli.jpg' },
  { name: 'Pesci', image: '/sos-icons/pesci.jpg' },
  { name: 'Altre Specie', image: '/sos-icons/altre-specie.png' },
  { name: 'Oggetti Grandi', image: '/sos-icons/oggetti.jpg' },
  { name: 'Reti e Cime', image: '/sos-icons/reti.jpg' }
];

const ANIMAL_STATUS_OPTIONS = [
  { value: 'vivo', label: 'Vivo', color: '#10B981' },
  { value: 'morto', label: 'Morto', color: '#EF4444' },
  { value: 'non_so', label: 'Non so', color: '#6B7280' }
];

const ANIMAL_LOCATION_OPTIONS = [
  { value: 'acqua', label: 'In acqua', color: '#0EA5E9' },
  { value: 'spiaggia', label: 'In spiaggia', color: '#F59E0B' }
];

const WOUNDS_OPTIONS = [
  { value: 'si', label: 'Sì', color: '#EF4444' },
  { value: 'no', label: 'No', color: '#10B981' },
  { value: 'non_visibili', label: 'Non visibili', color: '#6B7280' }
];

// Componente Map Picker per selezionare posizione
const MapPickerComponent = ({ initialLat, initialLng, onSelect }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [selectedPos, setSelectedPos] = useState({ lat: initialLat, lng: initialLng });

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Fix icone Leaflet
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    });

    const map = L.map(mapRef.current).setView([initialLat, initialLng], 10);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19
    }).addTo(map);

    // Marker iniziale
    const redIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="width:32px;height:32px;background:#EF4444;border-radius:50%;border:4px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.5);"></div>',
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });

    markerRef.current = L.marker([initialLat, initialLng], { icon: redIcon }).addTo(map);

    // Click su mappa per spostare marker
    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      markerRef.current.setLatLng([lat, lng]);
      setSelectedPos({ lat, lng });
    });

    // Forza resize dopo mount per evitare problemi di rendering
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [initialLat, initialLng]);

  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Mappa - occupa tutto lo spazio disponibile tranne il footer */}
      <div ref={mapRef} className="flex-1" style={{ minHeight: '200px' }} />
      
      {/* Footer fisso in basso con spazio extra per navigation bar */}
      <div className="bg-slate-800 p-4 pb-24 border-t border-slate-600">
        <p className="text-slate-400 text-sm mb-1">Posizione selezionata:</p>
        <p className="text-cyan-400 font-mono text-lg mb-3">
          {selectedPos.lat.toFixed(6)}, {selectedPos.lng.toFixed(6)}
        </p>
        <button
          onClick={() => onSelect(selectedPos.lat, selectedPos.lng)}
          data-testid="map-confirm-btn"
          className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-lg rounded-xl shadow-lg"
        >
          ✓ CONFERMA POSIZIONE
        </button>
      </div>
    </div>
  );
};

const SOSSection = ({ gpsLocation: initialGpsLocation, onGoHome }) => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [gpsLocation, setGpsLocation] = useState(initialGpsLocation || { lat: 0, lng: 0 });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showProtocolPopup, setShowProtocolPopup] = useState(true);
  const [coastGuardInfo, setCoastGuardInfo] = useState(COAST_GUARD_NUMBERS['IT']); // Default Italia
  const [detectingCountry, setDetectingCountry] = useState(false);
  const watchIdRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Guida Emme
  const { showGuide, closeGuide, openGuide } = useEmmeGuide('sos');
  
  // NUOVO: Scelta posizione all'inizio
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [showInitialMapPicker, setShowInitialMapPicker] = useState(false);
  const manualLocationSetRef = useRef(false); // useRef per essere visibile nella closure di watchPosition

  // Campi dettaglio
  const [animalStatus, setAnimalStatus] = useState('');
  const [animalLocation, setAnimalLocation] = useState('');
  const [hasWounds, setHasWounds] = useState('');
  const [hasNets, setHasNets] = useState(null);
  
  // Foto emergenza
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [compressing, setCompressing] = useState(false);
  
  // Modifica posizione manuale
  const [editingLocation, setEditingLocation] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [showMapPicker, setShowMapPicker] = useState(false);
  const mapPickerRef = useRef(null);
  const mapPickerInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Non aggiornare se l'utente ha già selezionato manualmente
          if (manualLocationSetRef.current) {
            console.log('GPS getCurrentPosition: BLOCCATO (manualLocationSetRef = true)');
            return;
          }
          console.log('GPS getCurrentPosition: Aggiornamento posizione automatico');
          setGpsLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          // ANCHE il callback di errore deve rispettare manualLocationSetRef!
          if (manualLocationSetRef.current) {
            console.log('GPS Error callback: BLOCCATO (manualLocationSetRef = true)');
            return;
          }
          console.log('GPS Error callback:', error.message);
          // Solo se non abbiamo una posizione valida
          setGpsLocation(prev => {
            if (!prev || (prev.lat === 0 && prev.lng === 0)) {
              return { lat: 0, lng: 0 };
            }
            return prev;
          });
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );

      // Watch position - aggiorna SOLO se NON è stata impostata manualmente
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          // Non aggiornare se l'utente ha impostato manualmente la posizione
          if (manualLocationSetRef.current) {
            console.log('GPS watchPosition: BLOCCATO (manualLocationSetRef = true)');
            return;
          }
          
          setGpsLocation(prev => {
            // Aggiorna solo se la differenza è > 0.0005 gradi (~50 metri)
            const latDiff = Math.abs((prev?.lat || 0) - position.coords.latitude);
            const lngDiff = Math.abs((prev?.lng || 0) - position.coords.longitude);
            if (latDiff > 0.0005 || lngDiff > 0.0005) {
              console.log('GPS watchPosition: Aggiornamento (delta significativo)');
              return {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
            }
            return prev;
          });
        },
        () => {},
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 10000 }
      );
    }

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  // Rileva il paese dalla posizione GPS usando reverse geocoding
  useEffect(() => {
    const detectCountry = async () => {
      if (!gpsLocation || gpsLocation.lat === 0 || gpsLocation.lng === 0) return;
      
      setDetectingCountry(true);
      try {
        // Usa OpenStreetMap Nominatim per reverse geocoding (gratuito)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${gpsLocation.lat}&lon=${gpsLocation.lng}&accept-language=en`,
          { headers: { 'User-Agent': 'RespiroBluApp/1.0' } }
        );
        
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.address?.country_code?.toUpperCase();
          
          if (countryCode && COAST_GUARD_NUMBERS[countryCode]) {
            setCoastGuardInfo(COAST_GUARD_NUMBERS[countryCode]);
          } else {
            // Fallback al numero europeo
            setCoastGuardInfo(COAST_GUARD_NUMBERS['DEFAULT']);
          }
        }
      } catch (error) {
        console.log('Could not detect country, using default');
        // In caso di errore, mantieni il default (Italia o precedente)
      } finally {
        setDetectingCountry(false);
      }
    };
    
    detectCountry();
  }, [gpsLocation.lat, gpsLocation.lng]);

  const handlePhotoSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    setCompressing(true);
    try {
      // Compressione ottimizzata: max 1024px, qualità 65%
      const compressed = await compressImage(file, 1024, 0.65);
      setPhoto(compressed.base64);
      setPhotoPreview(compressed.base64);
      toast.success(`Foto compressa: ${compressed.sizeKB}KB`);
    } catch (error) {
      console.error('Compression error:', error);
      toast.error('Errore compressione foto');
    } finally {
      setCompressing(false);
    }
  }, []);

  const removePhoto = useCallback(() => {
    setPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
    setShowDetailsForm(true);
  };

  const saveAndShowEmergency = async (category) => {
    const currentLocation = { ...gpsLocation };
    console.log('DEBUG: Saving SOS with location:', currentLocation);
    console.log('DEBUG: manualLocationSetRef.current:', manualLocationSetRef.current);
    setSaving(true);
    setShowEmergency(true);
    setShowDetailsForm(false);

    try {
      const sosData = {
        category: category || selectedCategory,
        location: currentLocation,
        animal_status: animalStatus || '',
        animal_location: animalLocation || '',
        has_wounds: hasWounds || '',
        has_nets: hasNets,
        photo_url: photo || null
      };
      
      console.log('DEBUG: SOS Data being sent:', JSON.stringify(sosData, null, 2));

      await axios.post(
        `${BACKEND_URL}/api/sos`,
        sosData,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );
      console.log('SOS salvato');
      toast.success('Segnalazione salvata!');
    } catch (error) {
      console.warn('Salvataggio SOS non riuscito, utente non bloccato');
    } finally {
      setSaving(false);
    }
  };

  const callCoastGuard = () => {
    // Rimuovi spazi dal numero per la chiamata
    const phoneNumber = coastGuardInfo.number.replace(/\s/g, '');
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleGoHome = () => {
    setShowEmergency(false);
    setShowDetailsForm(false);
    setSelectedCategory(null);
    setAnimalStatus('');
    setAnimalLocation('');
    setHasWounds('');
    setHasNets(null);
    setPhoto(null);
    setPhotoPreview(null);
    onGoHome();
  };

  const handleBackToCategories = () => {
    setShowDetailsForm(false);
    setSelectedCategory(null);
    setAnimalStatus('');
    setAnimalLocation('');
    setHasWounds('');
    setHasNets(null);
    setPhoto(null);
    setPhotoPreview(null);
  };

  const selectedCategoryData = categories.find(c => c.name === selectedCategory);
  const isAnimalCategory = selectedCategory && !['Oggetti Grandi', 'Reti e Cime'].includes(selectedCategory);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Guida Emme */}
      {showGuide && <EmmeGuide sectionId="sos" onClose={closeGuide} />}
      <EmmeHelpButton onClick={openGuide} />
      
      {/* POPUP PROTOCOLLO ANIMALI */}
      {showProtocolPopup && !showDetailsForm && !showEmergency && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 pt-8 overflow-y-auto" onClick={() => setShowProtocolPopup(false)}>
          <div className="bg-slate-900 border border-red-500/50 rounded-2xl p-6 max-w-md w-full mt-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-red-400">Protocollo Animali Marini</h3>
            </div>
            <div className="space-y-3 text-slate-300 text-sm">
              <p>
                <strong className="text-white">È obbligatorio chiamare la Guardia Costiera</strong> (numero blu <span className="text-cyan-400 font-bold">1530</span>) in caso di rinvenimento di animali marini (cetacei, tartarughe) spiaggiati, sia vivi che morti.
              </p>
              <p>
                La segnalazione è <strong className="text-white">fondamentale</strong> per attivare la rete di soccorso specializzata e gestire correttamente l'emergenza, rispettando le norme di sicurezza e sanitarie.
              </p>
            </div>
            
            {/* Info numero - dinamico in base al paese */}
            <div className="mt-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
              {detectingCountry ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Rilevamento posizione...</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-cyan-400 mb-1">
                    <Phone className="w-5 h-5" />
                    <span className="font-semibold">{coastGuardInfo.name}:</span>
                    <span className="text-white font-bold text-lg">{coastGuardInfo.number}</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-500 text-xs">
                    <Globe className="w-3 h-3" />
                    <span>{coastGuardInfo.country}</span>
                  </div>
                </>
              )}
            </div>
            
            <button
              type="button"
              onClick={() => setShowProtocolPopup(false)}
              className="w-full mt-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg"
            >
              Ho capito, procedi
            </button>
          </div>
        </div>
      )}

      {/* SCELTA POSIZIONE INIZIALE */}
      {!locationConfirmed && !showDetailsForm && !showEmergency && !showProtocolPopup && (
        <div className="fixed inset-0 bg-slate-950 z-40 flex flex-col">
          <div className="bg-slate-900 p-4 flex items-center gap-3 border-b border-slate-700">
            <button onClick={onGoHome} className="p-2 text-slate-400 hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-white font-bold text-lg">Posizione Segnalazione</h2>
          </div>
          
          <div className="flex-1 p-6 flex flex-col">
            <p className="text-slate-300 mb-6">
              Dove si trova l&apos;animale o l&apos;oggetto da segnalare?
            </p>
            
            {/* Opzione 1: Posizione attuale */}
            <button
              onClick={() => {
                setLocationConfirmed(true);
              }}
              className="w-full p-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl mb-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-500/30 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold">Usa posizione attuale</p>
                  <p className="text-emerald-200 text-sm">
                    {gpsLocation?.lat ? `${gpsLocation.lat.toFixed(4)}, ${gpsLocation.lng.toFixed(4)}` : 'Rilevamento GPS...'}
                  </p>
                </div>
              </div>
            </button>
            
            {/* Opzione 2: Scegli dalla mappa */}
            <button
              onClick={() => setShowInitialMapPicker(true)}
              className="w-full p-4 bg-slate-800 hover:bg-slate-700 border border-cyan-500/50 rounded-xl text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center">
                  <Map className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-white font-bold">Scegli dalla mappa</p>
                  <p className="text-slate-400 text-sm">Tocca il punto esatto sulla mappa</p>
                </div>
              </div>
            </button>
            
            <p className="text-slate-500 text-xs mt-4 text-center">
              Puoi modificare la posizione anche in seguito
            </p>
          </div>
          
          {/* Map Picker per scelta iniziale */}
          {showInitialMapPicker && (
            <div className="fixed inset-0 bg-black z-50 flex flex-col">
              {/* Header */}
              <div className="bg-slate-900 p-4 flex items-center justify-between flex-shrink-0">
                <h3 className="text-white font-bold">Tocca la mappa per scegliere</h3>
                <button onClick={() => setShowInitialMapPicker(false)} className="text-white p-2">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {/* Map Container - posizione relativa per il MapPickerComponent absolute */}
              <div className="flex-1 relative">
                <MapPickerComponent 
                  initialLat={gpsLocation?.lat || 42.5}
                  initialLng={gpsLocation?.lng || 12.5}
                  onSelect={(lat, lng) => {
                    console.log('=== SELEZIONE MANUALE MAPPA ===');
                    console.log('Coordinate selezionate:', lat, lng);
                    
                    // PRIMA imposta il flag per bloccare GPS
                    manualLocationSetRef.current = true;
                    console.log('manualLocationSetRef impostato a TRUE');
                    
                    // POI aggiorna la posizione
                    setGpsLocation({ lat, lng });
                    console.log('gpsLocation aggiornato a:', { lat, lng });
                    
                    // Chiudi e conferma
                    setShowInitialMapPicker(false);
                    setLocationConfirmed(true);
                    toast.success('Posizione selezionata manualmente!');
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* SELEZIONE CATEGORIA */}
      {locationConfirmed && !showDetailsForm && !showEmergency && (
        <>
          <div className="mb-6">
            <button
              onClick={onGoHome}
              className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              Torna alla Dashboard
            </button>
          </div>

          <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-red-500" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                SOS EMERGENZA
              </h2>
              <p className="text-slate-400 mt-2">Tocca una categoria per segnalare</p>
            </div>

            {/* GPS Status - altezza fissa per evitare salti */}
            <div className="h-14 mb-6">
              {gpsLocation && gpsLocation.lat !== 0 && (
                <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 text-sm font-semibold">GPS Attivo</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  data-testid={`sos-category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleCategoryClick(cat.name)}
                  className="h-32 rounded-xl border-2 border-slate-700 hover:border-red-500 bg-slate-800/50 flex flex-col items-center justify-center gap-2 p-3 transition-all hover:scale-105 active:scale-95"
                >
                  <img src={cat.image} alt={cat.name} className="w-16 h-16 object-contain" />
                  <span className="text-white font-bold text-sm">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* FORM DETTAGLI */}
      {showDetailsForm && !showEmergency && (
        <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6">
          <button
            onClick={handleBackToCategories}
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Cambia categoria
          </button>

          {/* Categoria selezionata */}
          <div className="flex items-center justify-center gap-4 mb-6 p-4 bg-red-900/20 rounded-xl border border-red-500/30">
            {selectedCategoryData && (
              <img src={selectedCategoryData.image} alt={selectedCategory} className="w-16 h-16 object-contain" />
            )}
            <div>
              <p className="text-slate-400 text-sm">Segnalazione:</p>
              <h3 className="text-2xl font-bold text-red-400" style={{fontFamily: 'Barlow Condensed'}}>
                {selectedCategory}
              </h3>
            </div>
          </div>

          {/* FOTO EMERGENZA */}
          <div className="mb-6">
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Camera className="w-4 h-4 text-red-400" />
              Foto Emergenza (consigliata)
            </label>
            
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg border border-red-500/30" />
                <button 
                  type="button" 
                  onClick={removePhoto} 
                  className="absolute top-2 right-2 p-2 bg-red-500/80 rounded-full text-white hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={compressing}
                className="w-full py-4 border-2 border-dashed border-red-600/50 rounded-lg text-slate-400 hover:border-red-500 hover:text-red-400 transition-all flex flex-col items-center gap-2 bg-red-900/10"
              >
                {compressing ? (
                  <>
                    <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Compressione...</span>
                  </>
                ) : (
                  <>
                    <Camera className="w-8 h-8" />
                    <span className="text-sm font-medium">Scatta o Scegli Foto Emergenza</span>
                    <span className="text-xs text-slate-500">Aiuta i soccorritori a valutare la situazione</span>
                  </>
                )}
              </button>
            )}
            <input 
              ref={fileInputRef} 
              type="file" 
              accept="image/*" 
              onChange={handlePhotoSelect} 
              className="hidden" 
            />
          </div>

          {/* Campi solo per categorie animali */}
          {isAnimalCategory && (
            <>
              <p className="text-slate-400 text-center mb-4 text-sm">
                Informazioni aggiuntive per aiutare i soccorritori
              </p>

              <div className="space-y-5">
                {/* Animale vivo o morto */}
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-400" />
                    L'animale è vivo o morto?
                  </label>
                  <div className="flex gap-2">
                    {ANIMAL_STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAnimalStatus(opt.value)}
                        className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium transition-all ${
                          animalStatus === opt.value 
                            ? 'text-white' 
                            : 'bg-slate-800/50 text-slate-400 hover:text-white'
                        }`}
                        style={animalStatus === opt.value ? { backgroundColor: opt.color } : {}}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* In acqua o spiaggia */}
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Waves className="w-4 h-4 text-cyan-400" />
                    Dove si trova?
                  </label>
                  <div className="flex gap-2">
                    {ANIMAL_LOCATION_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAnimalLocation(opt.value)}
                        className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium transition-all ${
                          animalLocation === opt.value 
                            ? 'text-white' 
                            : 'bg-slate-800/50 text-slate-400 hover:text-white'
                        }`}
                        style={animalLocation === opt.value ? { backgroundColor: opt.color } : {}}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Presenza ferite */}
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-orange-400" />
                    Presenza di ferite?
                  </label>
                  <div className="flex gap-2">
                    {WOUNDS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setHasWounds(opt.value)}
                        className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium transition-all ${
                          hasWounds === opt.value 
                            ? 'text-white' 
                            : 'bg-slate-800/50 text-slate-400 hover:text-white'
                        }`}
                        style={hasWounds === opt.value ? { backgroundColor: opt.color } : {}}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Presenza reti */}
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Anchor className="w-4 h-4 text-purple-400" />
                    Reti o cime sull'animale?
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setHasNets(true)}
                      className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium transition-all ${
                        hasNets === true ? 'bg-red-500 text-white' : 'bg-slate-800/50 text-slate-400'
                      }`}
                    >
                      Sì, presenti
                    </button>
                    <button
                      type="button"
                      onClick={() => setHasNets(false)}
                      className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium transition-all ${
                        hasNets === false ? 'bg-emerald-500 text-white' : 'bg-slate-800/50 text-slate-400'
                      }`}
                    >
                      No, assenti
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Info per categorie non-animali */}
          {!isAnimalCategory && (
            <p className="text-slate-400 text-center mb-4 text-sm">
              Scatta una foto per aiutare i soccorritori a identificare l'oggetto
            </p>
          )}

          {/* Pulsante Salva */}
          <button
            onClick={() => saveAndShowEmergency()}
            disabled={saving || compressing}
            className="w-full mt-6 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-xl rounded-xl transition-all shadow-[0_0_20px_rgba(239,68,68,0.4)] disabled:opacity-50"
            style={{fontFamily: 'Barlow Condensed'}}
          >
            {saving ? 'SALVATAGGIO...' : photo ? 'INVIA CON FOTO' : 'INVIA SEGNALAZIONE'}
          </button>
        </div>
      )}

      {/* SCHERMATA EMERGENZA */}
      {showEmergency && (
        <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center">
              {selectedCategoryData ? (
                <img src={selectedCategoryData.image} alt={selectedCategory} className="w-16 h-16 object-contain" />
              ) : (
                <AlertTriangle className="w-16 h-16 text-red-500" />
              )}
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              EMERGENZA {selectedCategory?.toUpperCase()}
            </h2>
            <p className="text-slate-400">
              {photo ? 'Segnalazione con foto registrata' : 'Segnalazione registrata'}
            </p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-cyan-500/30 mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-slate-400 text-sm">Coordinate GPS:</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowMapPicker(true)}
                  className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 bg-emerald-500/20 px-2 py-1 rounded"
                >
                  <Map className="w-3 h-3" />
                  Scegli da mappa
                </button>
                <button
                  onClick={() => {
                    setManualLat(gpsLocation?.lat?.toString() || '');
                    setManualLng(gpsLocation?.lng?.toString() || '');
                    setEditingLocation(!editingLocation);
                  }}
                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                >
                  <MapPin className="w-3 h-3" />
                  {editingLocation ? 'Annulla' : 'Manuale'}
                </button>
              </div>
            </div>
            
            {editingLocation ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500">Latitudine</label>
                  <input
                    type="number"
                    step="0.000001"
                    value={manualLat}
                    onChange={(e) => setManualLat(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-cyan-500/50 rounded-lg text-cyan-400 font-mono text-lg"
                    placeholder="es. 45.123456"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Longitudine</label>
                  <input
                    type="number"
                    step="0.000001"
                    value={manualLng}
                    onChange={(e) => setManualLng(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-cyan-500/50 rounded-lg text-cyan-400 font-mono text-lg"
                    placeholder="es. 12.654321"
                  />
                </div>
                <button
                  onClick={() => {
                    const lat = parseFloat(manualLat);
                    const lng = parseFloat(manualLng);
                    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                      setGpsLocation({ lat, lng });
                      setEditingLocation(false);
                      toast.success('Posizione aggiornata!');
                    } else {
                      toast.error('Coordinate non valide');
                    }
                  }}
                  className="w-full py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg"
                >
                  Salva Posizione
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-cyan-400 font-bold text-4xl font-mono" data-testid="gps-lat">
                  {gpsLocation?.lat?.toFixed(6) || '0.000000'}
                </p>
                <p className="text-cyan-400 font-bold text-4xl font-mono" data-testid="gps-lng">
                  {gpsLocation?.lng?.toFixed(6) || '0.000000'}
                </p>
              </div>
            )}
          </div>

          {/* Map Picker Modal */}
          {showMapPicker && (
            <div className="fixed inset-0 bg-black z-50 flex flex-col">
              <div className="bg-slate-900 p-4 flex items-center justify-between flex-shrink-0">
                <h3 className="text-white font-bold">Tocca per scegliere posizione</h3>
                <button onClick={() => setShowMapPicker(false)} className="text-white p-2">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 relative">
                <MapPickerComponent 
                  initialLat={gpsLocation?.lat || 42.5}
                  initialLng={gpsLocation?.lng || 12.5}
                  onSelect={(lat, lng) => {
                    console.log('=== SELEZIONE MANUALE MAPPA (Emergenza) ===');
                    // PRIMA imposta il flag per bloccare GPS
                    manualLocationSetRef.current = true;
                    // POI aggiorna la posizione
                    setGpsLocation({ lat, lng });
                    setShowMapPicker(false);
                    toast.success('Posizione aggiornata!');
                  }}
                />
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              data-testid="call-coast-guard-btn"
              onClick={callCoastGuard}
              className="w-full py-5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-2xl rounded-xl transition-all shadow-[0_0_25px_rgba(16,185,129,0.5)] flex items-center justify-center gap-3"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              <Phone className="w-8 h-8" />
              CHIAMA GUARDIA COSTIERA (1530)
            </button>

            <button
              data-testid="return-home-btn"
              onClick={handleGoHome}
              className="w-full py-5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xl rounded-xl transition-all"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              TORNA ALLA HOME
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOSSection;
